<template>
<div class="adj-generator" :class="{ 'adj-generator-embedded': embedded }">
    <EventInfoModal :is-show.sync="open_event_info"></EventInfoModal>
    <CallbackInfoModal :is-show.sync="open_callback_info"></CallbackInfoModal>

    <div v-if="!page_loaded" class="adj-loading">Loading...</div>

    <template v-else>
        <div v-if="!embedded" class="adj-header">
            <h1>Adjust Link Generator</h1>
        </div>

        <div class="adj-card">
            <form @submit="buildUrl">
                <div class="adj-grid-2">
                    <div class="adj-field">
                        <label class="adj-label">Type</label>
                        <div class="adj-seg-group">
                            <button v-for="opt in type_options" :key="opt.value" type="button" class="adj-seg" :class="{ active: form.type === opt.value }" @click="form.type = opt.value">{{ opt.text }}</button>
                        </div>
                    </div>
                    <div class="adj-field">
                        <label class="adj-label">OS</label>
                        <div class="adj-seg-group">
                            <button v-for="opt in os_options" :key="opt.value" type="button" class="adj-seg" :class="{ active: form.os === opt.value }" @click="form.os = opt.value">{{ opt.text }}</button>
                        </div>
                    </div>
                </div>

                <div class="adj-field">
                    <label class="adj-label">Link ID</label>
                    <input v-model="form.link_id" required type="text" class="adj-input" />
                </div>

                <div class="adj-field">
                    <label class="adj-label">Ad Group</label>
                    <input v-model="form.ad_group" type="text" class="adj-input" placeholder="{sub6}" />
                </div>

                <div class="adj-grid-2">
                    <div class="adj-field">
                        <label class="adj-label">Campaign</label>
                        <div class="adj-seg-group">
                            <button v-for="opt in campaign_options" :key="opt.value" type="button" class="adj-seg" :class="{ active: form.campaign === opt.value }" @click="form.campaign = opt.value">{{ opt.text }}</button>
                        </div>
                        <input v-if="form.campaign == 'manual'" v-model="campaign" type="text" class="adj-input" placeholder="Campaign name" />
                    </div>
                    <div class="adj-field">
                        <label class="adj-label">Creative</label>
                        <div class="adj-seg-group">
                            <button v-for="opt in creative_options" :key="opt.value" type="button" class="adj-seg" :class="{ active: form.creative === opt.value }" @click="form.creative = opt.value">{{ opt.text }}</button>
                        </div>
                        <input v-if="form.creative == 'manual'" required v-model="creative" type="text" class="adj-input" placeholder="Creative name" />
                    </div>
                </div>

                <div class="adj-field">
                    <label class="adj-label">Cost Type</label>
                    <div class="adj-seg-group">
                        <button v-for="opt in cost_type_options" :key="opt.value" type="button" class="adj-seg" :class="{ active: form.cost_type === opt.value }" @click="form.cost_type = opt.value">{{ opt.text }}</button>
                    </div>
                </div>

                <div class="adj-field">
                    <label class="adj-label">Tracker Limit</label>
                    <div class="adj-checkbox-row">
                        <b-form-checkbox v-model="form.tracker_limit"></b-form-checkbox>
                        <input v-if="form.tracker_limit" required v-model="tracker_limit" type="text" class="adj-input adj-input-inline" />
                    </div>
                </div>

                <!-- (free text) (example: https://go.postback-offer.com/postback?clickid={clickid}&goal=FTD)& -->
                <div class="adj-field">
                    <label class="adj-label">Install Callback</label>
                    <select class="adj-input adj-select" v-model="form.install_callback" required>
                        <option v-for="domain in domains_list" :key="domain" :value="domain">{{ domain }}</option>
                    </select>
                </div>

                <div class="adj-events">
                    <div v-for="event, index in event_callbackes" :key="index" class="adj-event-card">
                        <button v-if="index !== 0" type="button" class="adj-event-remove" @click.prevent="deleteEvent(index)">
                            <b-icon icon="trash"></b-icon>
                        </button>

                        <div class="adj-field">
                            <label class="adj-label">Event Name</label>
                            <div class="adj-input-with-icon">
                                <input v-model="event.event_name" required type="text" class="adj-input" />
                                <b-icon @click="open_event_info = !open_event_info" class="adj-icon-info" icon="exclamation-circle-fill" variant="warning"></b-icon>
                            </div>
                        </div>

                        <div class="adj-field">
                            <label class="adj-label">Event Callback</label>
                            <select class="adj-input adj-select" v-model="event.event_callback" required>
                                <option v-for="domain in domains_list" :key="domain" :value="domain">{{ domain }}</option>
                            </select>
                        </div>

                        <div class="adj-field">
                            <label class="adj-label">Event Callback Token</label>
                            <div class="adj-input-with-icon">
                                <input v-model="event.event_callback_token" required type="text" class="adj-input" />
                                <b-icon @click="open_callback_info = !open_callback_info" class="adj-icon-info" icon="exclamation-circle-fill" variant="warning"></b-icon>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="adj-field">
                    <button type="button" @click.prevent="addEvents" :disabled="!enable_add_btn" class="adj-add-event-btn">+ Add Event Callback</button>
                </div>

                <button type="submit" class="adj-generate-btn">Generate Link</button>
            </form>
        </div>

        <div v-if="hasGeneratedLinks && passed_validation" class="adj-results">
            <div class="adj-results-header">
                <h2>{{ impressionResultLink && clickResultLink ? 'Generated links' : 'Generated link' }}</h2>
            </div>
            <div v-if="clickResultLink" class="adj-result-card">
                <div class="adj-link-kind">Click (CTA)</div>
                <textarea readonly :value="clickResultLink" class="adj-link-code" @focus="onInputFocus"></textarea>
            </div>
            <div v-if="impressionResultLink" class="adj-result-card adj-result-card--imp">
                <div class="adj-link-kind adj-link-kind--imp">Imp (VTA)</div>
                <textarea readonly :value="impressionResultLink" class="adj-link-code adj-link-code--imp" @focus="onInputFocus"></textarea>
            </div>
        </div>
    </template>
</div>
</template>
<script>
import EventInfoModal from './EventInfoModal.vue'
import CallbackInfoModal from './CallbackInfoModal.vue'

export  default {
    name: 'AdjustLinkGeneratorCore',
    components: {
        EventInfoModal,
        CallbackInfoModal
    },
    props: {
        embedded: {
            type: Boolean,
            default: false,
        },
        initial: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            page_loaded: true,
            type_options: [],
            campaign_options: []
,           creative_options: [],
            os_options: [],
            cost_type_options: [],
            form: {
                type: 'https://view.adjust.com/impression/',
                link_id: '',
                ad_group: '{sub15}{sub6}{sub20}',
                campaign: '{sub15}{sub20}',
                creative: '320X50_{sub19}',
                os: 'android',
                cost_type: 'cpa',
                tracker_limit: '',
                install_callback: '',
                
            },
            campaign: '',
            creative: '',
            tracker_limit: '',
            url_template: {
                ios: '<Type><Link_ID>?adgroup=<Ad_Group>&campaign=<Campaign>&creative=<Creative>&os_name=ios&cost_type=<Cost_Type>&idfa={sub5}&ip_address={ip}&install_callback=<Install_Callback>',
                android : '<Type><Link_ID>?adgroup=<Ad_Group>&campaign=<Campaign>&creative=<Creative>&os_name=android&cost_type=<Cost_Type>&install_callback=<Install_Callback>'
            },
            resultLink: null,
            clickResultLink: null,
            impressionResultLink: null,
            passed_validation: false,
            CLICK_TYPE: 'https://app.adjust.com/',
            IMPRESSION_TYPE: 'https://view.adjust.com/impression/',
            domains_list: [
                '',
                'www.s2x2q.com', 
                'www.xwiw2.com', 
                'offers-imali.affise.com', 
                'go.app-back-offer.com', 
                'offers-apptimus.affise.com', 
                'go.postback-offer-2.com', 
                'go.postback-offer.com'
            ],
            open_event_info: false,
            open_callback_info: false,
            // enable_add_btn: false,
            event_callbackes: [
                {
                    event_callback: '',
                    event_callback_token: '',
                    event_name: '',
                }
            ],
        }
    },
    async created() {
        this.page_loaded = false;
        await this.initDataOptions();
        this.applyInitial(this.initial);
        this.page_loaded = true;

    },
    watch: {
        ['tracker_limit'](newValue) {
            if (newValue !== null) {
                const result = newValue.toString().replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                // this.$set(this.tracker_limit,'tracker_limit' ,result);
                this.tracker_limit = result;
            }
        },
        ['form.install_callback'] (newValue) {
            if (newValue !== '') {
                console.log(newValue);
                this.event_callbackes[0].event_callback = newValue;
            }
        }
    },
    computed: {
        hasGeneratedLinks() {
            return !!(this.clickResultLink || this.impressionResultLink || this.resultLink);
        },
        canApply() {
            return !!(this.hasGeneratedLinks && this.passed_validation);
        },
        enable_add_btn() {
            return this.event_callbackes[0].event_callback !== '' && this.event_callbackes[0].event_callback_token !== '' && this.event_callbackes[0].event_name !== '';
        }
    },
    methods: {
        applyInitial(initial) {
            if (!initial || typeof initial !== 'object') return;
            if (initial.type) {
                this.form.type = initial.type;
            }
            if (initial.os === 'ios' || initial.os === 'android') {
                this.form.os = initial.os;
            }
            if (initial.cost_type === 'cpa' || initial.cost_type === 'cpi') {
                this.form.cost_type = initial.cost_type;
            }
        },
        applyLinks() {
            if (!this.canApply) return;
            const click = this.clickResultLink || '';
            const impression = this.impressionResultLink || null;
            const hasBoth = !!(click && impression);
            this.$emit('apply', {
                links: [{
                    account: 'Adjust',
                    click,
                    impression,
                }],
                linkType: hasBoth ? 'both' : (impression ? 'impression' : 'click'),
                resultLink: impression || click || this.resultLink,
            });
        },
        async initDataOptions() {
            this.campaign_options = [{text:'Auto', value:'{sub15}{sub20}'}, {text:'Manual', value:'manual'}];
            this.creative_options = [{text:'Auto', value:'320X50_{sub19}'}, {text:'Manual', value:'manual'}];
            this.os_options = [{text:'Android', value:'android'}, {text:'iOS', value:'ios'}];
            this.cost_type_options = [{text:'CPA', value:'cpa'}, {text:'CPI', value:'cpi'}]
            this.type_options = [
                { text: 'Impression', value: this.IMPRESSION_TYPE },
                { text: 'Click', value: this.CLICK_TYPE },
            ];
        },
        async addEvents() {
            this.event_callbackes.push({
                event_callback: this.event_callbackes[0].event_callback,
                event_callback_token: '',
                event_name: '',
            });
        },
        buildLinkForType(type, params) {
            const {
                os,
                link_id,
                ad_group,
                campaign,
                creative,
                cost_type,
                tracker_limit,
                install_callback,
                event_callbackes,
            } = params;

            let resultLink = this.url_template[os]
                .replace('<Type>', type)
                .replace('<Link_ID>', link_id)
                .replace('<Ad_Group>', ad_group)
                .replace('<Campaign>', campaign)
                .replace('<Creative>', creative)
                .replace('<Cost_Type>', cost_type)
                .replace('<Install_Callback>', install_callback);

            const callbackTemplate = '&event_callback_<Event_Callback_Token>=<Event_Callback>';
            let eventsString = '';
            for (let i = 0; i < event_callbackes.length; i++) {
                let event_callback = event_callbackes[i].event_callback;
                const event_callback_token = event_callbackes[i].event_callback_token;
                const event_name = event_callbackes[i].event_name;

                event_callback = `https%3A%2F%2F${event_callback}%2Fpostback%3Fclickid%3D{clickid}%26goal%3D${event_name}`;
                eventsString += callbackTemplate
                    .replace('<Event_Callback>', event_callback)
                    .replace('<Event_Callback_Token>', event_callback_token);
            }
            resultLink += eventsString;

            if (tracker_limit) {
                resultLink += `&tracker_limit=${tracker_limit}`;
            }
            return resultLink;
        },
        async buildUrl(e) {
            e.preventDefault();
            this.passed_validation = true;

            // Remove spaces from everywhere
            this.campaign = this.campaign.replaceAll(' ', '');
            this.creative = this.creative.replaceAll(' ', '');
            this.form.link_id = this.form.link_id.replaceAll(' ', '');
            for (let k in this.event_callbackes) {
                this.event_callbackes[k].event_callback = this.event_callbackes[k].event_callback.replaceAll(' ', '');
                this.event_callbackes[k].event_callback_token = this.event_callbackes[k].event_callback_token.replaceAll(' ', '');
                this.event_callbackes[k].event_name = this.event_callbackes[k].event_name.replaceAll(' ', '');
            }

            let {
                type,
                os,
                link_id,
                ad_group,
                campaign,
                creative,
                cost_type,
                tracker_limit,
                install_callback,
            } = { ...this.form };

            const event_callbackes = this.event_callbackes;

            if (tracker_limit && this.tracker_limit !== '') {
                tracker_limit = this.tracker_limit.replaceAll(',', '');
            }

            if (creative === 'manual' && this.creative !== '') {
                creative = this.creative;
            }

            if (campaign === 'manual' && this.campaign !== '') {
                campaign = this.campaign;
            }

            install_callback = `https%3A%2F%2F${install_callback}%2Fpostback%3Fclickid%3D{clickid}%26goal%3DInstall`;

            const params = {
                os,
                link_id,
                ad_group,
                campaign,
                creative,
                cost_type,
                tracker_limit,
                install_callback,
                event_callbackes,
            };

            const isImpression = String(type).indexOf('impression') !== -1;
            if (isImpression) {
                // Impression mode generates both CTA (click) and VTA (impression) URLs
                this.clickResultLink = this.buildLinkForType(this.CLICK_TYPE, params);
                this.impressionResultLink = this.buildLinkForType(this.IMPRESSION_TYPE, params);
                this.resultLink = this.impressionResultLink;
            } else {
                this.clickResultLink = this.buildLinkForType(this.CLICK_TYPE, params);
                this.impressionResultLink = null;
                this.resultLink = this.clickResultLink;
            }

            this.$emit('can-apply', true);
        },
        async deleteEvent(index) {
            this.event_callbackes.splice(index, 1);
        },
        async validateForm() {
            const containsWhitespace = /\s+/;
            let failed = false;
            let failed_input = '';

            if (containsWhitespace.test(this.form.type)) {
                failed = true;
                failed_input = 'Type';
            } else if (containsWhitespace.test(this.form.link_id)) {
                failed = true;
                failed_input = 'Link Id';
            } else if (containsWhitespace.test(this.form.ad_group)) {
                failed = true;
                failed_input = 'Ad Group';
           
            } else if (this.form.campaign === 'manual' && containsWhitespace.test(this.form.campaign)) {
                failed = true;
                failed_input = 'Campaign';
            } else if (this.form.creative === 'manual' && containsWhitespace.test(this.form.creative)) {
                failed = true;
                failed_input = 'Creative';
            } else if (this.form.tracker_limit && containsWhitespace.test(this.form.tracker_limit)) {
                failed = true;
                failed_input = 'Tracker Limit';
            } else if (containsWhitespace.test(this.form.os)) {
                failed = true;
                failed_input = 'OS';
            } else if (containsWhitespace.test(this.form.cost_type)) {
                failed = true;
                failed_input = 'Cost Type';
            } else if (containsWhitespace.test(this.form.install_callback)) {
                failed = true;
                failed_input = 'Install Callback';
            } else if (containsWhitespace.test(this.form.event_callback)) {
                failed = true;
                failed_input = 'Event Callback';
            }

            if (failed) {
                this.notifyError(`Error - ${failed_input} contains spaces.`);
                return true;
            }
            return false;

        },
        onInputFocus(v) {
            v.target.select && v.target.select()
        },
    },

    
}

</script>
<style lang="css" scoped>
.adj-generator {
    min-height: 100%;
    padding: 24px;
    color: #0f172a;
    font-family: Inter, system-ui, -apple-system, sans-serif;
    box-sizing: border-box;
    text-align: left;
}

.adj-generator-embedded {
    min-height: 0;
    padding: 0;
}

.adj-loading {
    color: #475569;
    padding: 24px 0;
}

.adj-header {
    margin-bottom: 18px;
}

.adj-header h1 {
    font-size: 2rem;
    line-height: 1.1;
    font-weight: 800;
    margin: 0;
}

.adj-card {
    border-radius: 20px;
    padding: 20px 24px;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid rgba(15, 23, 42, 0.1);
    box-shadow: 0 18px 60px rgba(15, 23, 42, 0.1);
    backdrop-filter: blur(10px);
    text-align: left;
}

.adj-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin-block: 10px;
}

.adj-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    justify-content: start;
}

@media (max-width: 640px) {
    .adj-grid-2 {
        grid-template-columns: 1fr;
    }
}

.adj-field {
    margin-top: 0;
    margin-bottom: 16px;
    text-align: left;
}

.adj-seg-group {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-start;
}

.adj-seg {
    padding: 8px 12px;
    font-size: 0.875rem;
    border-radius: 10px;
    border: 1px solid rgba(148, 163, 184, 0.65);
    background: rgba(255, 255, 255, 0.92);
    color: #334155;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
    text-align: left;
}

.adj-seg:hover {
    border-color: rgba(14, 165, 233, 0.5);
}

.adj-seg.active {
    background: linear-gradient(135deg, #0ea5e9, #6366f1);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 10px 18px rgba(14, 165, 233, 0.22);
}

.adj-input,
.adj-select {
    display: block;
    width: 100%;
    max-width: 420px;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.98);
    border: 1px solid rgba(148, 163, 184, 0.55);
    color: #0f172a;
    font-size: 0.875rem;
    outline: none;
    box-sizing: border-box;
    text-align: left;
    margin-top: 8px;
}

.adj-input:focus,
.adj-select:focus {
    border-color: rgba(14, 165, 233, 0.85);
    box-shadow: 0 0 0 0.18rem rgba(14, 165, 233, 0.22);
}

.adj-select {
    cursor: pointer;
}

.adj-checkbox-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}

.adj-input-inline {
    margin-top: 0;
}

.adj-input-with-icon {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 420px;
    margin-top: 8px;
}

.adj-input-with-icon .adj-input {
    margin-top: 0;
}

.adj-icon-info {
    flex-shrink: 0;
    color: #f59e0b;
    cursor: pointer;
}

.adj-events > * + * {
    margin-top: 16px;
}

.adj-event-card {
    position: relative;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 14px;
    padding: 16px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
    margin-bottom: 16px;
}

.adj-event-card .adj-field:last-child {
    margin-bottom: 0;
}

.adj-event-remove {
    position: absolute;
    top: 12px;
    right: 12px;
    border: none;
    background: none;
    color: #dc2626;
    cursor: pointer;
    padding: 4px;
}

.adj-add-event-btn {
    padding: 8px 14px;
    font-size: 0.8125rem;
    font-weight: 500;
    border-radius: 10px;
    border: 1px solid rgba(148, 163, 184, 0.6);
    background: rgba(255, 255, 255, 0.92);
    color: #0284c7;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
}

.adj-add-event-btn:hover:not(:disabled) {
    border-color: rgba(14, 165, 233, 0.5);
}

.adj-add-event-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.adj-generate-btn {
    display: inline-block;
    width: auto;
    padding: 12px 24px;
    border-radius: 10px;
    background: linear-gradient(135deg, #10b981, #22c55e);
    border: none;
    color: #fff;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;
}

.adj-generate-btn:hover {
    filter: brightness(1.03);
}

.adj-results {
    margin-top: 24px;
}

.adj-results-header {
    margin-bottom: 12px;
}

.adj-results-header h2 {
    font-size: 0.875rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin: 0;
}

.adj-result-card {
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 14px;
    padding: 16px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.adj-result-card + .adj-result-card {
    margin-top: 12px;
}

.adj-result-card--imp {
    background: rgba(241, 245, 249, 0.95);
}

.adj-link-kind {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #475569;
    margin-bottom: 8px;
}

.adj-link-kind--imp {
    color: #64748b;
}

.adj-link-code {
    display: block;
    width: 100%;
    resize: vertical;
    min-height: 146px;
    font-size: 0.75rem;
    color: #0f172a;
    background: #f8fafc;
    border: 1px solid rgba(148, 163, 184, 0.5);
    border-radius: 8px;
    padding: 10px;
    word-break: break-all;
    font-family: ui-monospace, monospace;
    line-height: 1.6;
    white-space: pre-wrap;
    box-sizing: border-box;
}

.adj-link-code--imp {
    color: #64748b;
    background: #f1f5f9;
}
</style>